/* eslint-disable react-hooks/exhaustive-deps */
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useMemo, useState } from "react";
import { Formater, TypeRecords } from "src/framework/commons/Formater";
import { FlatButton } from "src/framework/components/buttons/FlatButton";
import { Projeto, ProjetosDescontos, ProjetosDescontosHistorico } from "../../types";
import { LocalStorage } from "src/framework/commons/LocalStorage";
import { ProjetosDescontosProvider } from "src/providers/ProjetosDescontosProvider";
import { IDataResponse, STATUS_CODE } from "src/framework/providers/api";
import { ProjetosDescontosLista, ProjetosDescontosRequest } from "src/providers/ProjetosDescontosProvider/types";
import { PermissionUtil } from "src/framework/commons/PermissionUtil";
import { useObservable } from "src/framework/store/storeUtil";
import { onConflictAction$ } from "src/store/vendas/projetos/store";
import { verificaPendenciaTrocaComponente } from "../../utils/verificaPendenciaTrocaComponente";
import { showPendenciaTrocaComponenteAlert } from "../../utils/showPendenciaTrocaComponenteAlert";
import adminStore from "src/store/AdminStore";
import { notifierStore } from "src/framework/store/NotifierStore";
import { confirmationDialog } from "src/framework/store/ConfirmationDialogStore";
import { LoginStore } from "src/framework/store/LoginStore";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { ErrorValidate } from "src/components/pagamento/types";
import { authUser$ } from "src/framework/components/navigations/MenuUser";

const projetosDescontosProvider = new ProjetosDescontosProvider();

export interface ModalAplicarDescontoProperties {
    openModal: boolean,
    projeto?: Projeto,
    onClose: () => void;
    onSuccess: () => void;
}

interface ErrorState {
    descontoDistribuidor: ErrorValidate
    descontoRevenda: ErrorValidate
    descontoIntelbras: ErrorValidate
    justificativa: ErrorValidate
}

const INITIAL_ERROR_STATE: ErrorState = {
    descontoDistribuidor: {
        hasError: false,
        hintText: ""
    },
    descontoRevenda: {
        hasError: false,
        hintText: ""
    },
    descontoIntelbras: {
        hasError: false,
        hintText: ""
    },
    justificativa: {
        hasError: false,
        hintText: ""
    },
}

const formatValueToStr = (value: number, fixed: number = 3): string => {
    if (Number.isInteger(value)) return value.toString();
    return Number(Number(value).toFixed(fixed)).toLocaleString('pt-BR')
}

const useStyles = makeStyles((_theme) => ({
  tooltipPopper: {
    marginBottom: '-14px',
  },
}));

const ModalAplicarDesconto: FC<ModalAplicarDescontoProperties> = ({
    openModal,
    projeto,
    onClose,
    onSuccess,
}) => {
    useEffect(()=>{
        onConflictAction$.next(() => {
            onSuccess();
            onClose();
        })
    },[onClose, onSuccess])

    useEffect(()=>{
        return () => {
            onConflictAction$.next(null);
        }
    },[])

    const userInfo = LocalStorage.get("solar.userExtraInfo", true);
    const authUser = useObservable(authUser$) || userInfo;
    const [projetoState, setProjetoState] = useState<Projeto>();
    const [projetoDesconto, setProjetoDesconto] = useState<ProjetosDescontos>();
    const [descontoDistribuidor, setDescontoDistribuidor] = useState<number>(0);
    const [descontoRevenda, setDescontoRevenda] = useState<number>(0);
    const [descontoIntelbras, setDescontoIntelbras] = useState<number>(0);
    const [justificativa, setJustificativa] = useState<string>("");
    const [valorProjetoItens, setValorProjetoItens] = useState<number>(0);
    const [valorProjetoItensComDesconto, setValorProjetoItensComDesconto] = useState<number>(0);
    const [valorDescontoIntelbras, setValorDescontoIntelbras] = useState<number>(0);
    const [tetoMaximoIntelbras, setTetoMaximoIntelbras] = useState<number>(0);
    const [maximoDescontoRevendaPercentual, setMaximoDescontoRevendaPercentual] = useState(0);
    const permissoes:string[] = useObservable(LoginStore.permissoes) || [];
    const [disableBotaoConfirmar, setDisableBotaoConfirmar] = useState<boolean>(false);
    const classes = useStyles();

    const [percentualComissaoDistribuidorOriginal, setPercentualComissaoDistribuidorOriginal] = useState(0)

    const [errorState, setErrorState] = useState<ErrorState>(INITIAL_ERROR_STATE);

    const comissaoProjeto = useMemo(()=>{
        return Number(percentualComissaoDistribuidorOriginal || 0) - Number(descontoDistribuidor || 0)
    },[descontoDistribuidor, percentualComissaoDistribuidorOriginal])

    const totalDescontos = useMemo(()=>{
        if (valorProjetoItens && valorProjetoItens !== 0) {
            return {
                percentual: ((valorProjetoItens - valorProjetoItensComDesconto)/valorProjetoItens)*100,
                valor: valorProjetoItens - valorProjetoItensComDesconto,
            }
        } else {
            return {
                percentual: 0,
                valor: 0,
            }
        }
    },[valorProjetoItens, valorProjetoItensComDesconto])
    
    useEffect(() => {
        if (openModal && projeto) {
            setProjetoState(projeto);
            
            (async () => {
                if (projeto.id) {

                    const response = await projetosDescontosProvider.consultarDescontos(projeto.id);

                    if (response.statusCode === STATUS_CODE.OK) {
                        setProjetoDesconto(response.data);
                        setDescontoDistribuidor(Number(response.data?.desconto_distribuidor) || 0);
                        setPercentualComissaoDistribuidorOriginal(Number(response.data?.percentual_comissao_distribuidor_original) || 0);
                        setDescontoRevenda(Number(response.data?.desconto_revenda) || 0);
                        setDescontoIntelbras(Number(response.data?.desconto_intelbras) || 0);
                        setValorProjetoItens(Number(response.data?.valor_produtos) || 0);
                        setValorDescontoIntelbras(Number(response.data?.valor_intelbras) || 0);
                        setTetoMaximoIntelbras(Number(response.data?.teto_maximo_intelbras) || 0);
                        setMaximoDescontoRevendaPercentual(response.data?.maximo_desconto_revenda_percentual || 0)

                        calcularValorItensComDesconto(response.data?.valor_produtos,
                            response.data?.desconto_distribuidor, response.data?.desconto_revenda, response.data?.desconto_intelbras, response.data?.valor_intelbras);
                    }
                }
            })();
        }
    }, [openModal]);

    const validateDescontos = (): boolean => {
        const errorMessages: string[] = []
        const errors = { ...errorState }

        if (authUser?.isRevenda && !projeto?.segmento?.includes("off")) {
            if (!descontoRevenda || descontoRevenda < 0) {
                errorMessages.push(`O campo "Desconto Revenda" é obrigatório.`);
                errors.descontoRevenda = { hasError: true, hintText: "" }
            }

            if (maximoDescontoRevendaPercentual > 0 && descontoRevenda > maximoDescontoRevendaPercentual) {
                errorMessages.push(`Limite máximo de desconto da revenda: ${maximoDescontoRevendaPercentual}%`);
                errors.descontoRevenda = { hasError: true, hintText: "" }
            }
        }

        if (authUser?.isDistribuidor) {
            if(descontoDistribuidor > percentualComissaoDistribuidorOriginal) {
                errorMessages.push(`Percentual de desconto informado é maior que o percentual de comissão, manutenção não permitida!`);
                errors.descontoDistribuidor = { hasError: true, hintText: "" }
            }
        }

        if (PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_INTELBRAS, permissoes)) {

            if(descontoIntelbras > tetoMaximoIntelbras){
                errorMessages.push(`Percentual de desconto informado (Intelbras) é maior que o limite de ${tetoMaximoIntelbras}%, manutenção não permitida!`);
                errors.descontoIntelbras = { hasError: true, hintText: "" }
            }

            if (authUser?.isIntelbras && (!justificativa || justificativa === "")) {
                errorMessages.push(`O campo "Justificativa" é obrigatório.`)
                errors.justificativa = { hasError: true, hintText: "" }
            }
        }

        if (errorMessages.length > 0) {
            setErrorState({ ...errors })
            notifierStore.setNotifier({
                title: "Aviso",
                content: errorMessages,
                timeOut: 3500,
                onClose: () => {
                    notifierStore.setOpen(false)
                }
            })
            notifierStore.setOpen(true)

            return false
        }

        return true;
    }

    const doSave = async (): Promise<void> => {
        const deconstosList: ProjetosDescontosLista[] = [
            {
                novoPercentual: descontoDistribuidor,
                tipo: "distribuidor",
                novoValor: 0,
            },
            {
                novoPercentual: projeto?.segmento?.includes("off") ? 0 : descontoRevenda,
                tipo: "revenda",
                novoValor: 0,
            },
            {
                novoPercentual: descontoIntelbras,
                tipo: "intelbras",
                novoValor: valorDescontoIntelbras,
                justificativa,
            },
        ];

        const requestDesconto: ProjetosDescontosRequest = {
            descontos: deconstosList,
        }

        const dataProjeto: Projeto | any = {...projetoState};
        if (projetoState?.id) {
            await adminStore.getProjetoTrocasPendentes(projetoState?.id).then((resp) => {
                if (resp && resp?.componentes) dataProjeto.componentes = resp?.componentes;
                if (resp && resp?.componentes_modificados) dataProjeto.componentes_modificados = resp?.componentes_modificados;
            });
        }

        if (verificaPendenciaTrocaComponente(dataProjeto)) {
            showPendenciaTrocaComponenteAlert();
            return;
        }

        if (projetoState?.id) {
            const resultRequest: IDataResponse = await projetosDescontosProvider.atualizarDescontos(projetoState.id, requestDesconto);
            if (resultRequest.statusCode === STATUS_CODE.OK) {
                notifierStore.setNotifier({
                    title: "Aviso!!!",
                    content: ["Atualização de Descontos concluída!"],
                    timeOut: 3500,
                    onClose: () => {
                        notifierStore.setOpen(false);
                        onSuccess();
                        onClose();
                    },
                    onFinishTimeout: () => {
                        onSuccess();
                        onClose();
                    }
                });
                notifierStore.setOpen(true);
            }
        }

    }

    const calcularValorItensComDesconto = (valorProjetoItens: number, descontoDistribuidor: number, descontoRevenda: number, descontoIntelbras: number, valorDescontoIntelbras: number): void => {
        const totalDesconto = ((Number(descontoDistribuidor || 0) + Number(descontoRevenda || 0) + Number(descontoIntelbras || 0)) * valorProjetoItens) / 100 ;
        setValorProjetoItensComDesconto(Number(valorProjetoItens) - (totalDesconto +  Number(valorDescontoIntelbras || 0)));
        if (Number(percentualComissaoDistribuidorOriginal || 0) - Number(descontoDistribuidor || 0) < 0) {
            setDisableBotaoConfirmar(true);
        } else if (authUser?.isRevenda && maximoDescontoRevendaPercentual && descontoRevenda > maximoDescontoRevendaPercentual) {
            setDisableBotaoConfirmar(true);
        } if(authUser?.isRevenda && projeto?.segmento?.includes("off")) {
            setDisableBotaoConfirmar(true);
        } else {
            setDisableBotaoConfirmar(false);
        }
    };

    const disableDiscount = useMemo(()=> {
        const semDesconto = (!Number(descontoDistribuidor) && !Number(descontoRevenda)  && !Number(descontoIntelbras) && !Number(valorDescontoIntelbras));
        if (semDesconto && (!projetoDesconto || !projetoDesconto?.historico?.length)) {
            return true;
        } else {
            return false;
        }
    },[projetoDesconto?.historico, descontoDistribuidor, descontoRevenda, descontoIntelbras, valorDescontoIntelbras]);

    const textoTooltip = useMemo(()=> {
        if (authUser?.isRevenda && projeto?.segmento?.includes("off")) {
            return "Não habilitado para Off Grid!";
        } else if (disableDiscount) {
            return "Nenhum desconto aplicado!";
        }

        return "";
    }, [authUser?.isRevenda, projeto?.segmento, disableDiscount]);

    return <>
        <Dialog
            open={openModal}
            onClose={onClose}
            maxWidth='md'
        >
            <DialogContent>
                <Grid container spacing={1}>
                    <Grid container size={12}>
                        <Grid size={12}>
                            <Box component={"h2"}>Aplicar Desconto - <span style={{ fontSize: 15, fontWeight: 'normal' }}>{projetoState?.codigo_venda}</span></Box>
                        </Grid>
                    </Grid>
                    <Grid container size={12}>
                        <Grid size={12}>

                            {(authUser?.isDistribuidor || authUser?.isIntelbras) && PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_DISTRIBUIDOR, permissoes) ? <>
                                <Grid size={12}>
                                    <InputDecimal3
                                        allowNegative={false}
                                        label={"Desconto Distribuidor (%)"}
                                        fullWidth={true}
                                        value={descontoDistribuidor}
                                        sufix="%"
                                        decimal={2}
                                        margin="normal"
                                        error={(percentualComissaoDistribuidorOriginal !== undefined && percentualComissaoDistribuidorOriginal !== null && descontoDistribuidor > percentualComissaoDistribuidorOriginal) || errorState.descontoDistribuidor.hasError}
                                        helperText={percentualComissaoDistribuidorOriginal !== undefined && percentualComissaoDistribuidorOriginal !== null && descontoDistribuidor > percentualComissaoDistribuidorOriginal ? `O valor não pode ser maior que ${formatValueToStr(percentualComissaoDistribuidorOriginal)}` : ''}
                                        onChange={(el: any) => {
                                            const value = el.target.value || 0

                                            setDescontoDistribuidor(value);
                                            const percentual: number = Number(value);
                                        
                                            calcularValorItensComDesconto(valorProjetoItens,
                                                percentual, descontoRevenda, descontoIntelbras, valorDescontoIntelbras);
                                                    
                                        }}
                                    />
                                </Grid>
                            </> : ""}
                            {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_REVENDA, permissoes) ? <>
                                <Grid container size={12}>
                                    {projeto?.segmento?.includes("off") ? 
                                        <Tooltip
                                            title="Não habilitado para Off Grid!"
                                            arrow
                                            placement="top-start"
                                            classes={{ popper: classes.tooltipPopper }}
                                            >
                                            <span style={{ display: 'inline-block', width: '100%' }}>
                                                <div style={{ pointerEvents: 'none', width: '100%' }}>
                                                <InputDecimal3
                                                    allowNegative={false}
                                                    label={"Desconto Revenda (%)"}
                                                    fullWidth
                                                    value={descontoRevenda}
                                                    sufix="%"
                                                    decimal={2}
                                                    disabled
                                                    margin="normal"
                                                />
                                                </div>
                                            </span>
                                        </Tooltip>
                                        : 
                                        <InputDecimal3
                                            allowNegative={false}
                                            label={"Desconto Revenda (%)"}
                                            fullWidth={true}
                                            value={descontoRevenda}
                                            sufix="%"
                                            decimal={2}
                                            disabled={projeto?.revenda === undefined || projeto?.segmento?.includes("off")}
                                            margin="normal"
                                            error={(authUser?.isRevenda && maximoDescontoRevendaPercentual && descontoRevenda > maximoDescontoRevendaPercentual) || errorState.descontoRevenda.hasError}
                                            helperText={authUser?.isRevenda && maximoDescontoRevendaPercentual && descontoRevenda > maximoDescontoRevendaPercentual ? `O valor não pode ser maior que ${maximoDescontoRevendaPercentual}%` : ''}
                                            onChange={(el: any) => {
                                                setDescontoRevenda(el.target.value);
    
                                                if(el.target.value){
                                                    calcularValorItensComDesconto(valorProjetoItens,
                                                        descontoDistribuidor, Number(el.target.value), descontoIntelbras, valorDescontoIntelbras);
                                                        
                                                }
                                            }}
                                        />
                                    }
                                </Grid>
                            </> : ""}

                            {(authUser?.isDistribuidor || authUser?.isIntelbras) && PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_DISTRIBUIDOR, permissoes) ? <>
                                <Grid size={12}>
                                    <InputText
                                        label={"Percentual do Distribuidor (%)"}
                                        fullWidth={true}
                                        value={Formater.toString(comissaoProjeto, TypeRecords.DECIMAL2)}
                                        sufix="%"
                                        margin="normal"
                                        disabled
                                    />
                                </Grid>
                            </> : ""}

                            {authUser?.isIntelbras && PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_INTELBRAS, permissoes) ? <>
                                <Grid size={12}>
                                    <InputDecimal3
                                        allowNegative={false}
                                        label={"Desconto Intelbras (%)"}
                                        fullWidth={true}
                                        value={descontoIntelbras}
                                        margin="normal"
                                        decimal={2}
                                        error={errorState.descontoIntelbras.hasError}
                                        disabled={!tetoMaximoIntelbras || tetoMaximoIntelbras < 1}
                                        onChange={(el: any) => {
                                            setDescontoIntelbras(el.target.value);
                                            
                                            if(el.target.value){
                                                setValorDescontoIntelbras(0);
                                                
                                                calcularValorItensComDesconto(valorProjetoItens,
                                                    descontoDistribuidor, descontoRevenda, Number(el.target.value), valorDescontoIntelbras);
                                            }
                                            
                                        }}
                                    />
                                    <InputDecimal3
                                        allowNegative={false}
                                        label={"Valor Desconto Intelbras"}
                                        fullWidth={true}
                                        value={valorDescontoIntelbras}
                                        margin="normal"
                                        decimal={2}
                                        disabled={!tetoMaximoIntelbras || tetoMaximoIntelbras < 1}
                                        onChange={(el: any) => {
                                            setValorDescontoIntelbras(el.target.value ? parseFloat(el.target.value) : 0);
                                            
                                            if(el.target.value){
                                                setDescontoIntelbras(0);

                                                calcularValorItensComDesconto(valorProjetoItens,
                                                    descontoDistribuidor, descontoRevenda, descontoIntelbras, Number(el.target.value));
                                            }
                                            
                                        }}
                                    />
                                </Grid>

                            </> : ""}
                        </Grid>
                        <Grid size={12}>
                            <InputText
                                label={"Valor dos Produtos"}
                                fullWidth={true}
                                value={Formater.toString(valorProjetoItens, TypeRecords.CURRENCY)}
                                disabled
                            />
                        </Grid>
                        <Grid size={12}>
                            <InputText
                                label={"Valor dos produtos com desconto"}
                                fullWidth={true}
                                value={Formater.toString(valorProjetoItensComDesconto, TypeRecords.CURRENCY)}
                                disabled
                            />
                        </Grid>
                        <Grid size={12}>
                            <InputText
                                label={"Valor total de desconto"}
                                fullWidth={true}
                                value={Formater.toString(totalDescontos.valor, TypeRecords.CURRENCY)}
                                disabled
                            />
                        </Grid>
                        <Grid size={12}>
                            <InputText
                                label={"Percentual total de desconto"}
                                fullWidth={true}
                                value={Formater.toString(totalDescontos.percentual, TypeRecords.DECIMAL2)}
                                sufix="%"
                                margin="normal"
                                disabled
                            />
                        </Grid>
                        
                        {authUser?.isIntelbras && PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_HISTORICO, permissoes)? <>
                            <Grid size={12}>
                                <InputText
                                    label={"Justificativa"}
                                    fullWidth={true}
                                    value={justificativa}
                                    error={errorState.justificativa.hasError}
                                    onChange={(el: any) => {
                                        //setNovoValorServico(el.target.value)
                                        setJustificativa(el.target.value);

                                        const errors = { ...errorState }
                                        if(el.target.value){
                                            errors.justificativa = { hasError: false, hintText: "" }
                                        }
                                        if(!el.target.value || el.target.value === ""){
                                            errors.justificativa = { hasError: true, hintText: "" }
                                        }

                                        setErrorState(errors);

                                    }}
                                />
                            </Grid>

                            <Grid size={12}>
                                <TableContainer>
                                    <Table size="medium">
                                        <TableHead>
                                            <TableRow>
                                                {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_DISTRIBUIDOR, permissoes) ? <>
                                                    <TableCell>Desconto Distribuidor</TableCell>
                                                </> : ""}
                                                {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_REVENDA, permissoes) ? <>
                                                    <TableCell>Desconto Revenda</TableCell>
                                                </> : ""}
                                                {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_INTELBRAS, permissoes) ? <>
                                                    <TableCell>Desconto Intelbras</TableCell>
                                                    <TableCell>Valor Desconto Intelbras</TableCell>
                                                </> : ""}
                                                <TableCell>Justificativa</TableCell>
                                                <TableCell>Data</TableCell>
                                                <TableCell>Usuário</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {projetoDesconto?.historico?.map((p: ProjetosDescontosHistorico) => {
                                                return <>
                                                    <TableRow>
                                                        {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_DISTRIBUIDOR, permissoes) ? <>
                                                            <TableCell>{Formater.toString(p.percentual_distribuidor || 0, TypeRecords.PERCENT2)}</TableCell>
                                                        </> : ""}
                                                        {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_REVENDA, permissoes) ? <>
                                                            <TableCell>{Formater.toString(p.percentual_revenda || 0, TypeRecords.PERCENT2)}</TableCell>
                                                        </> : ""}
                                                        {PermissionUtil.can(PermissionUtil.PERMISSION_ACAO_APLICAR_DESCONTO_INTELBRAS, permissoes) ? <>
                                                            <TableCell>{Formater.toString(p.percentual_intelbras || 0, TypeRecords.PERCENT2)}</TableCell>
                                                            <TableCell>{Formater.toString(p.valor_desconto_intelbras || 0, TypeRecords.CURRENCY)}</TableCell>
                                                        </> : ""}
                                                        <TableCell>{p.justificativa}</TableCell>
                                                        <TableCell>{Formater.toString(p.created_at, TypeRecords.DATE_TIME)}</TableCell>
                                                        <TableCell>{p.nome_usuario}</TableCell>
                                                    </TableRow>
                                                </>
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        </> : ""}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <FlatButton
                    color="inherit"
                    variant="contained"
                    disableElevation
                    text="Cancelar"
                    onClick={onClose}
                />
                <Tooltip
                    title={textoTooltip}
                    arrow
                    placement="top-start"
                    classes={{ popper: classes.tooltipPopper }}
                    >
                    <div>
                        <FlatButton
                            color="primary"
                            variant="contained"
                            disableElevation
                            disabled={disableBotaoConfirmar || disableDiscount}
                            text="Confirmar"
                            onClick={() => {

                                confirmationDialog.setup({
                                    title: "Aviso!!!",
                                    content: <>Deseja atualizar os descontos do projeto <strong>{projeto?.codigo_venda}</strong></>,
                                    onClose: () => {
                                        confirmationDialog.close();
                                    },
                                    onConfirmation: () => {
                                        if (validateDescontos()) {
                                            doSave();
                                        }
                                        confirmationDialog.close();
                                    }
                                })
                                confirmationDialog.open();
                            }}
                        />
                    </div>
                </Tooltip>
            </DialogActions>
        </Dialog >
    </>
}

export { ModalAplicarDesconto }